import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Hash, ArrowLeft, Loader } from 'lucide-react';
import PostCard from './PostCard';
import { useTheme } from '../context/ThemeContext';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.div`
  background: ${props => props.theme.cardBackground};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: ${props => props.theme.cardShadow};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  font-size: 14px;
  margin-bottom: 16px;
  padding: 4px 0;
  
  &:hover {
    color: ${props => props.theme.textPrimary};
  }
`;

const TagTitle = styled.h1`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 8px 0;
  font-size: 28px;
  color: ${props => props.theme.textPrimary};
`;

const TagStats = styled.p`
  color: ${props => props.theme.textSecondary};
  margin: 0;
  font-size: 14px;
`;

const PostsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: ${props => props.theme.textSecondary};
`;

const LoadMoreButton = styled.button`
  background: ${props => props.theme.buttonPrimary};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  margin: 20px auto;
  display: block;
  
  &:hover {
    background: ${props => props.theme.buttonPrimaryHover};
  }
  
  &:disabled {
    background: ${props => props.theme.border};
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${props => props.theme.textSecondary};
  
  h3 {
    margin-bottom: 8px;
    color: ${props => props.theme.textPrimary};
  }
`;

export default function TagPage() {
  const { tagName } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const fetchTagPosts = async (page = 1) => {
    try {
      if (page === 1) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      const response = await fetch(`/post/by-tag/${encodeURIComponent(tagName)}?page=${page}&limit=10`, {
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (data.success) {
        if (page === 1) {
          setPosts(data.posts);
        } else {
          setPosts(prev => [...prev, ...data.posts]);
        }
        setPagination(data.pagination);
      } else {
        setError(data.message || '게시물을 불러오는 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('태그 게시물 로드 오류:', error);
      setError('게시물을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (tagName) {
      fetchTagPosts(1);
    }
  }, [tagName]);

  const handleLoadMore = () => {
    if (pagination.page < pagination.totalPages) {
      fetchTagPosts(pagination.page + 1);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer theme={theme}>
          <Loader className="animate-spin" size={24} />
          <span style={{ marginLeft: '12px' }}>게시물을 불러오는 중...</span>
        </LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <EmptyState theme={theme}>
          <h3>오류가 발생했습니다</h3>
          <p>{error}</p>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header theme={theme}>
        <BackButton theme={theme} onClick={handleBack}>
          <ArrowLeft size={16} />
          뒤로 가기
        </BackButton>
        <TagTitle theme={theme}>
          <Hash size={28} />
          #{tagName}
        </TagTitle>
        <TagStats theme={theme}>
          {pagination.total}개의 게시물
        </TagStats>
      </Header>

      {posts.length > 0 ? (
        <>
          <PostsContainer>
            {posts.map((post) => (
              <PostCard 
                key={post.id} 
                post={post}
                onPostUpdated={(updatedPost) => {
                  setPosts(posts.map(p => p.id === updatedPost.id ? updatedPost : p));
                }}
                onPostDeleted={(deletedPostId) => {
                  setPosts(posts.filter(p => p.id !== deletedPostId));
                }}
              />
            ))}
          </PostsContainer>
          
          {pagination.page < pagination.totalPages && (
            <LoadMoreButton 
              theme={theme}
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? (
                <>
                  <Loader className="animate-spin" size={16} style={{ marginRight: '8px' }} />
                  로딩 중...
                </>
              ) : (
                '더 보기'
              )}
            </LoadMoreButton>
          )}
        </>
      ) : (
        <EmptyState theme={theme}>
          <Hash size={48} color={theme.border} />
          <h3>#{tagName} 태그의 게시물이 없습니다</h3>
          <p>이 태그로 작성된 게시물이 아직 없어요.</p>
        </EmptyState>
      )}
    </Container>
  );
}